import React from 'react';
import { observer } from 'mobx-react-lite';
import { Button } from 'src/domains/common/components/button/button';
import { Input } from 'src/domains/common/components/input/input';
import { Select } from 'src/domains/common/components/select/select';
import { Switch } from 'src/domains/common/components/switch/switch';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from 'src/domains/common/components/table/table';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { useCommon } from 'src/AppState/Common';
import { moneySymbol } from 'src_common/common/amount/website-money/currency';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { ValueWithOnClickType } from 'src/domains/common/components/select/select.state';
import { IndependentTooltip } from 'src/components/Tooltip/IndependentTooltip';
import {
    DEFAULT_DEPOSIT_LIMIT_CALCULATION_OPTIONS,
    DEFAULT_DEPOSIT_LIMIT_TYPE_OPTIONS,
    DefaultDepositLimitItemState,
    DefaultDepositLimitsState,
    isCustomDepositLimitWindow,
} from 'src/components/ComplianceConfiguration/DefaultDepositLimits/DefaultDepositLimits.state';
import {
    DefaultDepositLimitsActions,
    DefaultDepositLimitsAgeSwitch,
    DefaultDepositLimitsAmountCell,
    DefaultDepositLimitsAmountGroup,
    DefaultDepositLimitsAmountInput,
    DefaultDepositLimitsAmountLabel,
    DefaultDepositLimitsAmountLine,
    DefaultDepositLimitsAmountRow,
    DefaultDepositLimitsCalculationField,
    DefaultDepositLimitsCreateAmount,
    DefaultDepositLimitsCreateBlock,
    DefaultDepositLimitsCreateFields,
    DefaultDepositLimitsCreatePanel,
    DefaultDepositLimitsCurrencyPrefix,
    DefaultDepositLimitsCustomHint,
    DefaultDepositLimitsDaysField,
    DefaultDepositLimitsEmpty,
    DefaultDepositLimitsFormLayout,
    DefaultDepositLimitsLockedField,
    DefaultDepositLimitsLockedTooltipWrap,
    DefaultDepositLimitsLockedValue,
    DefaultDepositLimitsPanel,
    DefaultDepositLimitsPeriod,
    DefaultDepositLimitsPeriodCell,
    DefaultDepositLimitsRowLabel,
    DefaultDepositLimitsTableWrap,
    DefaultDepositLimitsToolbar,
    DefaultDepositLimitsUnavailable,
} from 'src/components/ComplianceConfiguration/DefaultDepositLimits/DefaultDepositLimits.style';

interface DefaultDepositLimitsFormPropsType {
    state: DefaultDepositLimitsState;
}

const AmountField = ({
    currencySymbol,
    dataTest,
    disabled,
    errors,
    formInputState,
    notSet = false,
}: {
    currencySymbol: string;
    dataTest: string;
    disabled: boolean;
    errors: Array<string>;
    formInputState: FormInputState<string, number>;
    notSet?: boolean;
}): JSX.Element => (
    <DefaultDepositLimitsAmountCell data-invalid={errors.length > 0 ? 'true' : undefined}>
        {notSet ? null : <DefaultDepositLimitsCurrencyPrefix>{currencySymbol}</DefaultDepositLimitsCurrencyPrefix>}
        <DefaultDepositLimitsAmountInput>
            <Input
                dataTest={dataTest}
                disabled={disabled || notSet}
                errors={errors}
                formInputState={formInputState}
                inputMode='decimal'
                placeholder='Not set'
                value={notSet ? '' : formInputState.value}
            />
        </DefaultDepositLimitsAmountInput>
    </DefaultDepositLimitsAmountCell>
);

const AgeSplitSwitch = ({
    checked,
    dataTest,
    disabled,
    onSplitChange,
}: {
    checked: boolean;
    dataTest: string;
    disabled: boolean;
    onSplitChange: (enabled: boolean) => void;
}): JSX.Element => (
    <DefaultDepositLimitsAgeSwitch title='Different amount for 25+'>
        <Switch
            aria-label='Different amount for 25+'
            checked={checked}
            dataTest={dataTest}
            disabled={disabled}
            label='Over 25 / Under 25'
            onChange={(event): void => {
                onSplitChange(event.target.checked);
            }}
        />
    </DefaultDepositLimitsAgeSwitch>
);

const LimitAmountEditor = observer(
    ({
        currencySymbol,
        amountDataTest,
        amountOverDataTest,
        splitDataTest,
        disabled,
        amount,
        amountErrors,
        amountOver,
        amountOverErrors,
        splitByAge,
        onSplitChange,
        notSet = false,
    }: {
        currencySymbol: string;
        amountDataTest: string;
        amountOverDataTest: string;
        splitDataTest: string;
        disabled: boolean;
        amount: FormInputState<string, number>;
        amountErrors: Array<string>;
        amountOver: FormInputState<string, number>;
        amountOverErrors: Array<string>;
        splitByAge: boolean;
        onSplitChange: (enabled: boolean) => void;
        notSet?: boolean;
    }): JSX.Element => (
        <DefaultDepositLimitsAmountGroup>
            {splitByAge ? (
                <>
                    <DefaultDepositLimitsAmountLine>
                        <DefaultDepositLimitsAmountLabel>Under 25</DefaultDepositLimitsAmountLabel>
                        <AmountField
                            currencySymbol={currencySymbol}
                            dataTest={amountDataTest}
                            disabled={disabled}
                            errors={amountErrors}
                            formInputState={amount}
                            notSet={notSet}
                        />
                    </DefaultDepositLimitsAmountLine>
                    <DefaultDepositLimitsAmountLine>
                        <DefaultDepositLimitsAmountLabel>Over 25</DefaultDepositLimitsAmountLabel>
                        <AmountField
                            currencySymbol={currencySymbol}
                            dataTest={amountOverDataTest}
                            disabled={disabled}
                            errors={amountOverErrors}
                            formInputState={amountOver}
                            notSet={notSet}
                        />
                    </DefaultDepositLimitsAmountLine>
                    <AgeSplitSwitch
                        checked={splitByAge}
                        dataTest={splitDataTest}
                        disabled={disabled || notSet}
                        onSplitChange={onSplitChange}
                    />
                </>
            ) : (
                <DefaultDepositLimitsAmountRow>
                    <AmountField
                        currencySymbol={currencySymbol}
                        dataTest={amountDataTest}
                        disabled={disabled}
                        errors={amountErrors}
                        formInputState={amount}
                        notSet={notSet}
                    />
                    <AgeSplitSwitch
                        checked={splitByAge}
                        dataTest={splitDataTest}
                        disabled={disabled || notSet}
                        onSplitChange={onSplitChange}
                    />
                </DefaultDepositLimitsAmountRow>
            )}
        </DefaultDepositLimitsAmountGroup>
    )
);

const calculationOptionsFor = (onGrossSelected: () => void): Array<ValueWithOnClickType<string>> =>
    DEFAULT_DEPOSIT_LIMIT_CALCULATION_OPTIONS.map((option) => ({
        ...option,
        onClick: (): void => {
            if (option.value === 'gross') {
                onGrossSelected();
            }
        },
    }));

const CUSTOM_LIMIT_LOCKED_TOOLTIP = 'This cannot be changed. Custom limits are always net rolling.';
const BOTH_CALCULATIONS_LOCKED_TOOLTIP = 'This cannot be changed. Net and gross limits already exist for this period.';

const LockedCustomLimitField = ({
    children,
    dataTest,
}: {
    children: React.ReactNode;
    dataTest: string;
}): JSX.Element => (
    <DefaultDepositLimitsLockedTooltipWrap>
        <IndependentTooltip
            content={CUSTOM_LIMIT_LOCKED_TOOLTIP}
            position='bottom'
            underline={false}
        >
            <DefaultDepositLimitsLockedField data-test={dataTest}>{children}</DefaultDepositLimitsLockedField>
        </IndependentTooltip>
    </DefaultDepositLimitsLockedTooltipWrap>
);

const LockedExistingLimitSelect = ({
    children,
    dataTest,
    tooltip,
}: {
    children: React.ReactNode;
    dataTest: string;
    tooltip: string;
}): JSX.Element => (
    <DefaultDepositLimitsLockedTooltipWrap>
        <IndependentTooltip
            content={tooltip}
            position='bottom'
            underline={false}
        >
            <DefaultDepositLimitsCalculationField data-test={dataTest}>{children}</DefaultDepositLimitsCalculationField>
        </IndependentTooltip>
    </DefaultDepositLimitsLockedTooltipWrap>
);

const DefaultDepositLimitItemRow = observer(
    ({
        item,
        currencySymbol,
        isSaving,
        periodLabelText,
        showPeriod,
        isGroupEnd,
        isLastGroup,
        isGrouped,
        isCalculationTypeLocked,
    }: {
        item: DefaultDepositLimitItemState;
        currencySymbol: string;
        isSaving: boolean;
        periodLabelText: string;
        showPeriod: boolean;
        isGroupEnd: boolean;
        isLastGroup: boolean;
        isGrouped: boolean;
        isCalculationTypeLocked: boolean;
    }): JSX.Element => {
        const isLifetime = item.windowDays === 0;
        const isCustomWindow = isCustomDepositLimitWindow(item.windowDays);
        const itemKey = `${item.windowDays}-${item.calculationTypeValue}`;
        const calculationLockTooltip = isCustomWindow ? CUSTOM_LIMIT_LOCKED_TOOLTIP : BOTH_CALCULATIONS_LOCKED_TOOLTIP;

        return (
            <TableRow
                className={isGrouped ? 'bg-muted/20' : undefined}
                data-group-end={isGroupEnd ? 'true' : undefined}
                data-last-group={isLastGroup ? 'true' : undefined}
                dataTest={`default-deposit-limits-item-${itemKey}`}
            >
                <TableCell className='align-middle overflow-visible'>
                    <DefaultDepositLimitsPeriodCell>
                        {showPeriod ? (
                            <DefaultDepositLimitsPeriod>{periodLabelText}</DefaultDepositLimitsPeriod>
                        ) : (
                            <span />
                        )}
                        <DefaultDepositLimitsRowLabel
                            data-test={`default-deposit-limits-item-label-${itemKey}`}
                            variant={item.calculationTypeValue}
                        >
                            {item.calculationTypeValue === 'gross' ? 'Gross' : 'Net'}
                        </DefaultDepositLimitsRowLabel>
                    </DefaultDepositLimitsPeriodCell>
                </TableCell>
                <TableCell>
                    <LimitAmountEditor
                        amount={item.amount}
                        amountDataTest={`default-deposit-limits-item-amount-${itemKey}`}
                        amountErrors={item.errors}
                        amountOver={item.amountOver}
                        amountOverDataTest={`default-deposit-limits-item-amount-over-${itemKey}`}
                        amountOverErrors={item.amountOverErrors}
                        currencySymbol={currencySymbol}
                        disabled={isSaving}
                        notSet={item.limitEnabled === false}
                        onSplitChange={item.setSplitByAge}
                        splitByAge={item.splitByAge}
                        splitDataTest={`default-deposit-limits-item-split-${itemKey}`}
                    />
                </TableCell>
                <TableCell className='overflow-visible'>
                    {isCalculationTypeLocked ? (
                        <LockedExistingLimitSelect
                            dataTest={`default-deposit-limits-item-calculation-${itemKey}`}
                            tooltip={calculationLockTooltip}
                        >
                            <Select
                                dataTest={`default-deposit-limits-item-calculation-${itemKey}`}
                                disabled={true}
                                errors={[]}
                                formInputState={item.calculationType}
                                isAutoPosition={true}
                                placeholder='Calculation'
                                values={calculationOptionsFor(item.setLimitTypeToCalendar)}
                            />
                        </LockedExistingLimitSelect>
                    ) : (
                        <DefaultDepositLimitsCalculationField>
                            <Select
                                dataTest={`default-deposit-limits-item-calculation-${itemKey}`}
                                disabled={isSaving}
                                errors={[]}
                                formInputState={item.calculationType}
                                isAutoPosition={true}
                                placeholder='Calculation'
                                values={calculationOptionsFor(item.setLimitTypeToCalendar)}
                            />
                        </DefaultDepositLimitsCalculationField>
                    )}
                </TableCell>
                <TableCell className='overflow-visible'>
                    {isLifetime ? (
                        <DefaultDepositLimitsPeriod>Lifetime</DefaultDepositLimitsPeriod>
                    ) : isCustomWindow ? (
                        <LockedExistingLimitSelect
                            dataTest={`default-deposit-limits-item-type-${itemKey}`}
                            tooltip={CUSTOM_LIMIT_LOCKED_TOOLTIP}
                        >
                            <Select
                                key={`default-deposit-limits-item-type-${itemKey}-${item.limitType.value}`}
                                dataTest={`default-deposit-limits-item-type-${itemKey}`}
                                disabled={true}
                                errors={[]}
                                formInputState={item.limitType}
                                isAutoPosition={true}
                                placeholder='Type'
                                values={DEFAULT_DEPOSIT_LIMIT_TYPE_OPTIONS}
                            />
                        </LockedExistingLimitSelect>
                    ) : (
                        <DefaultDepositLimitsCalculationField>
                            <Select
                                key={`default-deposit-limits-item-type-${itemKey}-${item.limitType.value}`}
                                dataTest={`default-deposit-limits-item-type-${itemKey}`}
                                disabled={isSaving || item.isLimitTypeLocked}
                                errors={[]}
                                formInputState={item.limitType}
                                isAutoPosition={true}
                                placeholder='Type'
                                values={DEFAULT_DEPOSIT_LIMIT_TYPE_OPTIONS}
                            />
                        </DefaultDepositLimitsCalculationField>
                    )}
                </TableCell>
                <TableCell>
                    <Switch
                        aria-label='Hard stop'
                        checked={item.hardLimit}
                        dataTest={`default-deposit-limits-item-hard-limit-${itemKey}`}
                        disabled={isSaving}
                        onChange={(event): void => {
                            item.setHardLimit(event.target.checked);
                        }}
                    />
                </TableCell>
                <TableCell>
                    {item.canSendEmail ? (
                        <Switch
                            aria-label='Send email'
                            checked={item.sendEmail}
                            dataTest={`default-deposit-limits-item-send-email-${itemKey}`}
                            disabled={isSaving}
                            onChange={(event): void => {
                                item.setSendEmail(event.target.checked);
                            }}
                        />
                    ) : (
                        <DefaultDepositLimitsUnavailable
                            data-test={`default-deposit-limits-item-send-email-${itemKey}`}
                        >
                            -
                        </DefaultDepositLimitsUnavailable>
                    )}
                </TableCell>
                <TableCell>
                    <Switch
                        aria-label={item.limitEnabled ? 'Enabled' : 'Disabled'}
                        checked={item.limitEnabled}
                        dataTest={`default-deposit-limits-item-enabled-${itemKey}`}
                        disabled={isSaving}
                        onChange={(event): void => {
                            item.setLimitEnabled(event.target.checked);
                        }}
                    />
                </TableCell>
            </TableRow>
        );
    }
);

export const DefaultDepositLimitsForm = observer(({ state }: DefaultDepositLimitsFormPropsType): JSX.Element => {
    const configState = ConfigState.get(useCommon());
    const currency = configState.config.currencies[0] ?? 'GBP';
    const currencySymbol = moneySymbol(currency);

    const handleSubmit = (event: React.FormEvent): void => {
        event.preventDefault();
        void state.saveConfiguration();
    };

    return (
        <DefaultDepositLimitsFormLayout
            data-test='default-deposit-limits-form'
            onSubmit={handleSubmit}
        >
            <DefaultDepositLimitsPanel data-test='default-deposit-limits-list'>
                <DefaultDepositLimitsToolbar data-test='default-deposit-limits-toolbar'>
                    <Button
                        aria-pressed={state.hideInactiveLimits}
                        dataTest='default-deposit-limits-hide-inactive'
                        icon='filter'
                        onClick={(): void => {
                            state.setHideInactiveLimits(state.hideInactiveLimits === false);
                        }}
                        size='sm'
                        type='button'
                        variant={state.hideInactiveLimits ? 'secondary' : 'outline'}
                    >
                        {state.hideInactiveLimits ? 'Show non-active limits' : 'Hide non-active limits'}
                    </Button>
                </DefaultDepositLimitsToolbar>
                <DefaultDepositLimitsTableWrap>
                    <Table
                        className='table-fixed w-full min-w-[940px] [&_th]:h-10 [&_th]:px-3 [&_td]:px-3 [&_td]:py-2.5 [&_td]:align-middle'
                        data-test='default-deposit-limits-table'
                    >
                        <TableHeader>
                            <TableRow>
                                <TableHead className='w-[240px]'>Period</TableHead>
                                <TableHead className='w-[34%]'>Amount</TableHead>
                                <TableHead className='w-[180px]'>Calculation</TableHead>
                                <TableHead className='w-[180px]'>Type</TableHead>
                                <TableHead
                                    className='w-[88px]'
                                    title='Block deposits when the limit is reached'
                                >
                                    Hard stop
                                </TableHead>
                                <TableHead
                                    className='w-[72px]'
                                    title='Send a warning email'
                                >
                                    Email
                                </TableHead>
                                <TableHead
                                    className='w-[72px]'
                                    title='Offer this period during signup'
                                >
                                    Active
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {state.visibleItemGroups.flatMap((group, groupIndex) =>
                                group.items.map((item, index) => (
                                    <DefaultDepositLimitItemRow
                                        key={item.id}
                                        currencySymbol={currencySymbol}
                                        isCalculationTypeLocked={state.isWindowCalculationLocked(item.windowDays)}
                                        isGroupEnd={index === group.items.length - 1}
                                        isGrouped={group.items.length > 1}
                                        isLastGroup={groupIndex === state.visibleItemGroups.length - 1}
                                        isSaving={state.isSaving}
                                        item={item}
                                        periodLabelText={group.periodLabel}
                                        showPeriod={index === 0}
                                    />
                                ))
                            )}
                            {state.visibleItemGroups.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <DefaultDepositLimitsEmpty data-test='default-deposit-limits-empty'>
                                            No default limits yet.
                                        </DefaultDepositLimitsEmpty>
                                    </TableCell>
                                </TableRow>
                            ) : null}
                        </TableBody>
                    </Table>
                </DefaultDepositLimitsTableWrap>
            </DefaultDepositLimitsPanel>

            <DefaultDepositLimitsActions>
                <Button
                    className='text-base'
                    dataTest='default-deposit-limits-save'
                    disabled={state.isSaving || state.hasValidationErrors}
                    icon='badge'
                    isLoading={state.isSaving}
                    size='lg'
                    type='submit'
                >
                    Save changes
                </Button>
            </DefaultDepositLimitsActions>

            <DefaultDepositLimitsCreatePanel data-test='default-deposit-limits-create'>
                <DefaultDepositLimitsCreateBlock data-test='default-deposit-limits-custom-add'>
                    <DefaultDepositLimitsCustomHint data-test='default-deposit-limits-custom-hint'>
                        Add a custom NET ROLLING limit that will be applied on sign up
                    </DefaultDepositLimitsCustomHint>
                    <DefaultDepositLimitsCreateFields>
                        <DefaultDepositLimitsDaysField>
                            <Input
                                dataTest='default-deposit-limits-custom-days'
                                disabled={state.isSaving}
                                errors={state.customWindowDaysErrors}
                                formInputState={state.customWindowDays}
                                inputMode='numeric'
                                max={366}
                                min={1}
                                placeholder='Days (1–366)'
                            />
                        </DefaultDepositLimitsDaysField>
                        <DefaultDepositLimitsCreateAmount>
                            <LimitAmountEditor
                                amount={state.customAmount}
                                amountDataTest='default-deposit-limits-custom-amount'
                                amountErrors={state.customAmountErrors}
                                amountOver={state.customAmountOver}
                                amountOverDataTest='default-deposit-limits-custom-amount-over'
                                amountOverErrors={state.customAmountOverErrors}
                                currencySymbol={currencySymbol}
                                disabled={state.isSaving}
                                onSplitChange={state.setCustomSplitByAge}
                                splitByAge={state.customSplitByAge}
                                splitDataTest='default-deposit-limits-custom-split'
                            />
                        </DefaultDepositLimitsCreateAmount>
                        <LockedCustomLimitField dataTest='default-deposit-limits-custom-calculation'>
                            <DefaultDepositLimitsRowLabel variant='net'>Net</DefaultDepositLimitsRowLabel>
                        </LockedCustomLimitField>
                        <LockedCustomLimitField dataTest='default-deposit-limits-custom-type'>
                            <DefaultDepositLimitsLockedValue>Rolling</DefaultDepositLimitsLockedValue>
                        </LockedCustomLimitField>
                        <Button
                            dataTest='default-deposit-limits-custom-add-button'
                            disabled={state.isSaving}
                            icon='plus'
                            isLoading={state.isSaving}
                            onClick={(): void => {
                                void state.addCustomNetRollingItem();
                            }}
                            size='sm'
                            type='button'
                            variant='secondary'
                        >
                            Add
                        </Button>
                    </DefaultDepositLimitsCreateFields>
                </DefaultDepositLimitsCreateBlock>
            </DefaultDepositLimitsCreatePanel>
        </DefaultDepositLimitsFormLayout>
    );
});
